import desktopApi from './desktopApi';
import type { OssUploadProgress } from './upload-service/oss.interface';

const APP_CENTER_ADMIN_BASE = 'v1/admin/app-center';

interface CreateAppAssetUploadSessionRequest {
  purpose: 'app_icon';
  appId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
}

interface AppAssetUploadSessionReply {
  uploadSignature?: {
    uploadUrl?: string;
    upload_url?: string;
    headers?: Record<string, string>;
    contentType?: string;
    content_type?: string;
    sizeBytes?: number | string;
    size_bytes?: number | string;
    sha256?: string;
  };
  upload_signature?: AppAssetUploadSessionReply['uploadSignature'];
  assetUrl?: string;
  asset_url?: string;
  objectKey?: string;
  object_key?: string;
  uploadConstraints?: {
    maxSizeBytes?: number | string;
    max_size_bytes?: number | string;
    contentType?: string;
    content_type?: string;
    sha256?: string;
  };
  upload_constraints?: AppAssetUploadSessionReply['uploadConstraints'];
}

interface UploadAppIconOptions {
  appId: string;
  file: File;
  sha256?: string;
  onProgress?: (progress: OssUploadProgress) => void;
}

function bytesToHex(buffer: ArrayBuffer) {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function numberValue(value: number | string | undefined) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasHeader(headers: Record<string, string>, name: string) {
  return Object.keys(headers).some(
    (key) => key.trim().toLowerCase() === name.toLowerCase(),
  );
}

function assertIconFile(file: File) {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('请选择 PNG、JPEG 或 WebP 图标');
  }
}

function appIconUploadFileName(contentType: string, sha256: string) {
  const ext =
    contentType === 'image/png'
      ? 'png'
      : contentType === 'image/jpeg'
        ? 'jpg'
        : contentType === 'image/webp'
          ? 'webp'
          : 'bin';
  return `icon-${sha256.slice(0, 12)}.${ext}`;
}

function normalizeSession(reply: AppAssetUploadSessionReply) {
  const signature = reply.uploadSignature ?? reply.upload_signature ?? {};
  const uploadConstraints =
    reply.uploadConstraints ?? reply.upload_constraints ?? {};
  return {
    uploadSignature: {
      uploadUrl: signature.uploadUrl ?? signature.upload_url ?? '',
      headers: signature.headers ?? {},
      contentType: signature.contentType ?? signature.content_type,
      sizeBytes: signature.sizeBytes ?? signature.size_bytes,
      sha256: signature.sha256,
    },
    assetUrl: reply.assetUrl ?? reply.asset_url ?? '',
    objectKey: reply.objectKey ?? reply.object_key ?? '',
    uploadConstraints: {
      maxSizeBytes:
        uploadConstraints.maxSizeBytes ?? uploadConstraints.max_size_bytes,
      contentType:
        uploadConstraints.contentType ?? uploadConstraints.content_type,
      sha256: uploadConstraints.sha256,
    },
  };
}

function assertUploadConstraints(
  session: ReturnType<typeof normalizeSession>,
  file: File,
  contentType: string,
  sha256: string,
) {
  const maxSizeBytes = numberValue(session.uploadConstraints.maxSizeBytes);
  if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
    throw new Error(`图标大小 ${file.size} 超过上传限制 ${maxSizeBytes}`);
  }

  const signedSize = numberValue(session.uploadSignature.sizeBytes);
  if (signedSize > 0 && file.size !== signedSize) {
    throw new Error(
      `图标大小 ${file.size} 与上传签名大小 ${signedSize} 不一致`,
    );
  }

  const signedContentType =
    session.uploadConstraints.contentType ??
    session.uploadSignature.contentType;
  if (signedContentType && signedContentType !== contentType) {
    throw new Error(
      `图标类型 ${contentType} 与上传签名类型 ${signedContentType} 不一致`,
    );
  }

  const signedSha256 =
    session.uploadConstraints.sha256 ?? session.uploadSignature.sha256;
  if (signedSha256 && signedSha256.toLowerCase() !== sha256) {
    throw new Error('图标 SHA256 与上传签名不一致');
  }
}

export async function calculateAppAssetFileSha256(file: File) {
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return bytesToHex(digest);
}

async function createUploadSession(data: CreateAppAssetUploadSessionRequest) {
  const response = (await desktopApi.post<AppAssetUploadSessionReply>(
    `${APP_CENTER_ADMIN_BASE}/asset-upload-sessions`,
    data,
  )) as unknown as AppAssetUploadSessionReply;
  return normalizeSession(response);
}

async function putFileToSignedUrl(
  file: File,
  session: ReturnType<typeof normalizeSession>,
  contentType: string,
  onProgress?: (progress: OssUploadProgress) => void,
) {
  const signature = session.uploadSignature;
  if (!signature.uploadUrl.trim()) {
    throw new Error('上传签名缺少 uploadUrl');
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signature.uploadUrl, true);

    const headers = signature.headers ?? {};
    for (const [key, value] of Object.entries(headers)) {
      const headerName = key.trim();
      if (headerName && value) {
        xhr.setRequestHeader(headerName, value);
      }
    }
    if (!hasHeader(headers, 'content-type')) {
      xhr.setRequestHeader('Content-Type', contentType);
    }

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : file.size;
      const loaded = event.loaded;
      onProgress?.({
        loaded,
        total,
        percent: total > 0 ? Math.min(100, (loaded / total) * 100) : 0,
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({
          loaded: file.size,
          total: file.size,
          percent: 100,
        });
        resolve();
        return;
      }

      reject(new Error(`上传失败，HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('上传失败，请检查网络'));
    xhr.onabort = () => reject(new Error('上传已取消'));
    xhr.send(file);
  });
}

export const appAssetUploadApi = {
  async uploadAppIcon({
    appId,
    file,
    sha256,
    onProgress,
  }: UploadAppIconOptions) {
    assertIconFile(file);
    const iconSha256 = sha256 ?? (await calculateAppAssetFileSha256(file));
    const session = await createUploadSession({
      purpose: 'app_icon',
      appId,
      fileName: appIconUploadFileName(file.type, iconSha256),
      contentType: file.type,
      sizeBytes: file.size,
      sha256: iconSha256,
    });

    assertUploadConstraints(session, file, file.type, iconSha256);
    await putFileToSignedUrl(file, session, file.type, onProgress);

    const assetUrl = session.assetUrl.trim();
    if (!assetUrl) {
      throw new Error('上传成功但未返回 asset URL');
    }
    return {
      assetUrl,
      objectKey: session.objectKey,
      sha256: iconSha256,
      sizeBytes: file.size,
      contentType: file.type,
      fileName: file.name,
    };
  },
};
